// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Meta } from '../types';
import { escapeHtml, fyShort } from '../utils/format';

/** About modal. Detached from the DOM when closed, mounted above the map. */
export function createAbout(meta: Meta) {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'About Waste & Recycling');

  let mounted = false;
  let timer: number | undefined;

  modal.innerHTML = `
    <button class="modal-close" aria-label="Close">✕</button>
    <h2>About Waste &amp; Recycling</h2>
    <p>This site turns Australia's <strong>National Waste Report</strong> — the country's authoritative
    waste dataset — into an interactive tool. It follows every tonne of waste from the material it's made
    of to where it ends up: recycled, turned into energy, reused, or sent to landfill.</p>

    <h3>What the numbers mean</h3>
    <p>Everything is measured in tonnes. The <strong>resource recovery rate</strong> is the share of waste
    kept out of landfill (recycling + energy recovery + reuse). Australia's national rate was
    ${escapeHtml(String(meta.national.recoveryRate))}% in ${escapeHtml(fyShort(meta.dataThrough))}; the
    National Waste Policy target is ${escapeHtml(String(meta.national.target))}% by 2030.</p>

    <h3>Where the data comes from</h3>
    <p>The <a href="${escapeHtml(meta.sources.dataset)}" target="_blank" rel="noopener">National Waste Report</a>,
    published by the Department of Climate Change, Energy, the Environment and Water, covering
    ${escapeHtml(fyShort(meta.dataFrom))} to ${escapeHtml(fyShort(meta.dataThrough))}
    (${escapeHtml(String(meta.yearCount))} reporting years). Two independently-published breakdowns are used —
    one by material category, one by waste stream — and reconciled against each other. Population figures for
    the per-person rates are from the <a href="${escapeHtml(meta.sources.abs)}" target="_blank" rel="noopener">ABS</a>.
    State boundaries are ABS ASGS polygons.</p>

    <h3>How it's checked</h3>
    <p>The pipeline runs two hard reconciliation gates: every row must use a known material, jurisdiction,
    fate and management label (a shifted column fails); and for the recent years where both breakdowns are
    complete (${escapeHtml(meta.reconciliation.completeYears.map(fyShort).join(', '))}), the two files' national
    totals must be equal to within a tonne (largest difference: ${escapeHtml(String(meta.reconciliation.maxAbsDiffTonnes))} t).</p>

    <h3>Caveats</h3>
    <ul>
      <li><strong>Core vs non-core:</strong> the totals include "non-core" waste — mainly fly ash from power
      stations and biosolids from sewage — which is very large and managed differently. It's counted here for
      completeness; each material is labelled.</li>
      <li><strong>Reporting lag:</strong> the National Waste Report is published biennially, so the latest data
      year is ${escapeHtml(fyShort(meta.dataThrough))}. Figures are estimates compiled from state data and vary
      in method between jurisdictions.</li>
      <li><strong>Streams:</strong> in the earliest years not all waste could be allocated to a stream, so the
      stream views are most complete for recent years.</li>
    </ul>

    <p style="margin-top:1rem;color:var(--text-tertiary);font-size:var(--font-size-sm)">
      National Waste Report data © Commonwealth of Australia, CC BY 4.0. Population and boundaries © ABS, CC BY.
      This is an independent project, not affiliated with the Australian Government.</p>
  `;

  const close = () => {
    overlay.classList.remove('open');
    modal.classList.remove('open');
    timer = window.setTimeout(() => { overlay.remove(); modal.remove(); mounted = false; }, 260);
  };
  const open = () => {
    if (!mounted) { window.clearTimeout(timer); document.body.append(overlay, modal); void modal.offsetWidth; mounted = true; }
    overlay.classList.add('open');
    modal.classList.add('open');
    (modal.querySelector('.modal-close') as HTMLElement)?.focus();
  };
  overlay.addEventListener('click', close);
  modal.querySelector('.modal-close')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && mounted) close(); });

  return { open, close };
}
